import { isEmpty } from 'lodash';
import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { useSWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';

import { useRecoilValueAfterMount } from '@/hooks/use-recoil-value-after-mount';
import { fluentProvider } from '@/lib/api/providers/fluent';
import { API_ERROR, MAILCHIMP_EVENTS } from '@/lib/constants';
import { transformRouteResults } from '@/lib/transformers/result';
import { carriersState } from '@/state/carriers';
import { featuresState } from '@/state/features';
import { linesState } from '@/state/lines';
import { locationsState } from '@/state/locations';
import { polylinesState } from '@/state/polylines';
import { globalRoutesReset, routesState, routesStatsState } from '@/state/routes';
import {
  errorMessageState,
  hasSearchedState,
  isSearchingRoutesState,
  searchLimitReachedState,
  searchResultsLayoutState,
} from '@/state/search';
import { vehiclesState } from '@/state/vehicles';
import { CargoOption, SearchQueryInterface } from '@/types';
import { PlaceInterface, RouteResultApiV4Interface } from '@/types/api-types';
import { request } from '@/utils/api/request';
import { createRouteSearchUrl, incrementUserSearchCount, isProductionEnv } from '@/utils/helpers';
import { getSearchHistoryUrl } from '@/utils/helpers/api-urls';
import { buildRoutesLayout } from '@/utils/layout';
import { logEvent } from '@/utils/logger';
import { parseAdvancedSearchParams } from '@/utils/search';
import { trackUser } from '@/utils/tracking';

const IS_NOT_PRODUCTION = !isProductionEnv();

export const useRouteSearch = ({
  origin,
  destination,
  advancedSearchParams,
  cargoParams,
}: {
  origin?: PlaceInterface;
  destination?: PlaceInterface;
  advancedSearchParams?: SearchQueryInterface;
  cargoParams?: CargoOption;
}) => {
  const setResultsLayout = useSetRecoilState(searchResultsLayoutState);
  const setRoutes = useSetRecoilState(routesState);
  const setRouteStats = useSetRecoilState(routesStatsState);
  const setLocations = useSetRecoilState(locationsState);
  const setCarriers = useSetRecoilState(carriersState);
  const setVehicles = useSetRecoilState(vehiclesState);
  const setLines = useSetRecoilState(linesState);
  const setPolylines = useSetRecoilState(polylinesState);
  const setHasSearched = useSetRecoilState(hasSearchedState);
  const setIsSearchingRoutes = useSetRecoilState(isSearchingRoutesState);
  const setErrorMessage = useSetRecoilState(errorMessageState);
  const setUserLimitReached = useSetRecoilState(searchLimitReachedState);
  const features = useRecoilValueAfterMount(featuresState, {});

  // State reset
  const resetRoutes = useResetRecoilState(globalRoutesReset);
  const { mutate: globalMutate } = useSWRConfig();

  const { data: session } = useSession();
  const userId = session?.user.id || '';
  const featuresArr = [
    features.includeRailRoutes && 'includeRailRoutes',
    features.pruneAirRoutes && 'pruneAirRoutes',
    features.pruneWaterRoutes && 'pruneWaterRoutes',
    features.useImprovedWaterSearch && 'useImprovedWaterSearch',
    features.useScheduleCheck && 'useScheduleCheck',
    features.pruneConverterAlternatives && 'pruneConverterAlternatives',
  ].filter(Boolean);

  const handleQuotaExceeded = () => {
    setUserLimitReached(true);

    // log an event that the anon search limit was hit
    trackUser.event('Route Search Limit Reached', {
      isAnon: !userId,
      userId,
    });

    if (session) {
      fluentProvider.triggerCrmEvent({
        email: session.user.email!,
        eventName: MAILCHIMP_EVENTS.SEARCH_LIMIT_REACHED,
      });
    }
  };

  const getRequestBody = () => ({
    body: { origin, destination, params: advancedSearchParams, cargo: cargoParams, features: featuresArr.join(',') },
  });

  const { isLoading, data, mutate } = useSWRImmutable<RouteResultApiV4Interface>(
    // NOTE: We create a route search url for SWR cache only. Search params for
    // the backend are passed via the request body
    () =>
      createRouteSearchUrl(origin, destination, {
        ...parseAdvancedSearchParams(advancedSearchParams),
        cargo: !isEmpty(cargoParams) ? JSON.stringify(cargoParams) : '',
        features: featuresArr.join(','),
      }),
    (url: string) => request(url, getRequestBody()),
    {
      errorRetryCount: 1,
      onError: (error) => {
        if (error.message === API_ERROR.QUOTA_EXCEEDED) {
          handleQuotaExceeded();
        } else {
          setErrorMessage(error.message || 'Error fetching data from our API');
          if (IS_NOT_PRODUCTION) {
            logEvent.error('Route Search Error', { error });
          }
        }
      },
    },
  );

  useEffect(() => {
    setHasSearched(true);

    if (!data || !data.routes) {
      resetRoutes();
      setErrorMessage('');
      setHasSearched(false);
      return;
    }

    // Transform results to desired shape
    const { routes, locations, carriers, vehicles, polylines, lines, stats } = transformRouteResults(data);

    setIsSearchingRoutes(false);

    // Set State
    setRoutes(routes);
    setRouteStats(stats);
    setLocations(locations);
    setCarriers(carriers);
    setVehicles(vehicles);
    setPolylines(polylines);
    setLines(lines);

    // Bust search history cache
    globalMutate(getSearchHistoryUrl());

    // GA conversion event
    trackUser.conversion({
      category: 'RoutePlanning',
      action: 'Searched',
    });
    // Track search event in browser, which expires at midnight
    incrementUserSearchCount();
  }, [data]);

  useEffect(() => {
    setIsSearchingRoutes(isLoading);
  }, [isLoading, setIsSearchingRoutes]);

  useEffect(() => {
    // Update the results layout any time origin, destination or advanced params
    // change
    setResultsLayout(buildRoutesLayout({ origin, destination, searchParams: advancedSearchParams, cargoParams }));
    setErrorMessage('');
  }, [advancedSearchParams, origin, destination, cargoParams]);

  const forceSearch = () => {
    mutate();
  };

  return {
    forceSearch,
  };
};
